import {
  Autocomplete,
  FormLabel,
  Stack,
  TextField,
  Typography,
  FormControl,
  type SxProps,
} from '@mui/material';
import { useState } from 'react';
import type React from 'react';

import { HelpIcon } from '@/components/shared/helpIcon';

import type { FieldValidation } from './validation/fieldValidation';
import { InputErrorIcon } from './validation/inputErrorIcon';

interface FormTextFieldBaseProps {
  size?: 'small' | 'medium';
  testId?: string;
  title: React.ReactNode;
  helpText?: React.ReactNode;
  alwaysShowHelp?: boolean;
  dataTestId?: string;
  validation?: FieldValidation;
  placeholder: string;
  value: string;
  sx?: SxProps;
  disabled?: boolean;
  autoFocus?: boolean;
  multiline?: boolean;
  rows?: number;
  maxLength?: number;
  type?: React.HTMLInputTypeAttribute;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  endAdornment?: React.ReactNode;
  showValidationError?: boolean;
}

// Free text stays possible with suggestions, so the two modes differ only in
// how the new value is reported: a picked suggestion has no input event.
type FormTextFieldProps = FormTextFieldBaseProps &
  (
    | {
        suggestions?: undefined;
        onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
        onValueChange?: undefined;
      }
    | {
        suggestions: string[];
        onChange?: undefined;
        onValueChange: (value: string) => void;
      }
  );

export const FormTextField = ({
  size = 'medium',
  testId,
  title,
  helpText,
  alwaysShowHelp = false,
  dataTestId,
  validation,
  placeholder,
  value,
  onChange,
  onValueChange,
  suggestions,
  sx,
  disabled = false,
  autoFocus = false,
  multiline = false,
  rows = 1,
  maxLength,
  type,
  inputProps,
  endAdornment,
  showValidationError = false,
}: FormTextFieldProps) => {
  const [touched, setTouched] = useState(false);
  const hasError =
    (touched || showValidationError) &&
    (validation ? !validation.validateInput(value) : false);

  const errorAdornment = hasError ? (
    <InputErrorIcon errorMessage={validation?.errorMessage(value)} />
  ) : (
    endAdornment
  );

  const htmlInputProps = {
    ...inputProps,
    ...(dataTestId ? { 'data-testid': dataTestId } : {}),
    ...(maxLength !== undefined ? { maxLength } : {}),
  };

  return (
    <FormControl className='group/help' sx={{ width: '100%', ...sx }}>
      {title && (
        <FormLabel>
          <Stack
            direction='row'
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant='subtitle1'>{title}</Typography>
            {helpText && (
              <HelpIcon
                title={helpText}
                alwaysVisible={alwaysShowHelp}
                className='text-(--mui-palette-text-secondary)'
              />
            )}
          </Stack>
        </FormLabel>
      )}
      {suggestions ? (
        <Autocomplete
          freeSolo
          size={size}
          disabled={disabled}
          value={value}
          options={suggestions}
          onBlur={() => setTouched(true)}
          onInputChange={(_, next) => onValueChange(next)}
          renderInput={(params) => (
            <TextField
              {...params}
              data-testid={testId}
              error={hasError}
              placeholder={placeholder}
              type={type}
              slotProps={{
                ...params.slotProps,
                htmlInput: { ...params.slotProps.htmlInput, ...htmlInputProps },
                input: {
                  ...params.slotProps.input,
                  endAdornment: (
                    <>
                      {errorAdornment}
                      {params.slotProps.input.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
      ) : (
        <TextField
          multiline={multiline}
          rows={rows}
          size={size}
          autoFocus={autoFocus}
          data-testid={testId}
          onBlur={() => setTouched(true)}
          error={hasError}
          sx={{
            '& .MuiInputBase-root': {
              paddingRight: hasError ? '4px' : '24px',
            },
            ...(multiline && {
              '& .MuiOutlinedInput-input': {
                paddingLeft: '0px',
              },
            }),
          }}
          slotProps={{
            htmlInput: htmlInputProps,
            input: {
              endAdornment: errorAdornment,
            },
          }}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          value={value}
          onChange={onChange}
        />
      )}
    </FormControl>
  );
};
